import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * What the dashboard category tree remembers between visits.
 *
 * The stored value is the set of *expanded* ids, so an untouched tree starts fully
 * collapsed simply by having nothing in it — and every branch someone opens is remembered,
 * including across a refresh.
 *
 * A `Record<string, true>` rather than a `Set`, because `persist` serialises through JSON
 * and a `Set` rehydrates as an empty object.
 */
interface UiDashboardTreeState {
  expandedIds: Record<string, true>;
  /** Drag-to-reorganise is opt-in, as it was in the old Vue tree. Navigating a tree and
   * restructuring it want different things from a click, and a stray drag while browsing
   * would silently reorganise someone's dashboards. Deliberately *not* persisted. */
  editMode: boolean;

  isExpanded: (dashboardCategoryId: string) => boolean;
  toggleExpanded: (dashboardCategoryId: string) => void;
  setExpanded: (dashboardCategoryId: string, expanded: boolean) => void;
  expandAll: (categoryIds: string[]) => void;
  collapseAll: () => void;
  /** Drops ids for categories that no longer exist, so the set can't grow forever as
   * categories come and go. Called once the real list is known. */
  pruneExpanded: (existingIds: string[]) => void;

  setEditMode: (editMode: boolean) => void;
  reset: () => void;
}

const useUiDashboardTreeState = create<UiDashboardTreeState>()(
  persist(
    (set, get) => ({
      expandedIds: {},
      editMode: false,

      isExpanded: (dashboardCategoryId) =>
        get().expandedIds[dashboardCategoryId] === true,

      toggleExpanded: (dashboardCategoryId) =>
        set((state) => {
          const next = { ...state.expandedIds };
          if (next[dashboardCategoryId]) delete next[dashboardCategoryId];
          else next[dashboardCategoryId] = true;
          return { expandedIds: next };
        }),

      setExpanded: (dashboardCategoryId, expanded) =>
        set((state) => {
          const next = { ...state.expandedIds };
          if (expanded) next[dashboardCategoryId] = true;
          else delete next[dashboardCategoryId];
          return { expandedIds: next };
        }),

      expandAll: (categoryIds) =>
        set(() => ({
          expandedIds: Object.fromEntries(
            categoryIds.map((id) => [id, true as const]),
          ),
        })),

      collapseAll: () => set(() => ({ expandedIds: {} })),

      pruneExpanded: (existingIds) =>
        set((state) => {
          const alive = new Set(existingIds);
          const next: Record<string, true> = {};
          let changed = false;

          for (const id of Object.keys(state.expandedIds)) {
            if (alive.has(id)) next[id] = true;
            else changed = true;
          }

          // Returning the same object when nothing was stale keeps this from being a
          // state update, which would otherwise re-render every subscriber on each load.
          return changed ? { expandedIds: next } : state;
        }),

      setEditMode: (editMode) => set(() => ({ editMode })),

      reset: () => set(() => ({ expandedIds: {}, editMode: false })),
    }),
    {
      name: "ui-dashboard-tree-state",
      // Only the expansion memory survives a reload. Edit mode is session state: coming
      // back to a tree that is silently in edit mode reads as something being broken.
      partialize: (state) => ({ expandedIds: state.expandedIds }),
    },
  ),
);

export default useUiDashboardTreeState;
